<script lang="ts">
import { defineBlock } from '@bagelink/blox'
import { $, defineSchema } from '@bagelink/vue'
const schema = defineSchema({
	title: $.text('Title'),
	variant: $.select('Archive Type', [
		{ label: 'Series and Symposia', value: 'series' },
		{ label: 'Printable Readers', value: 'printable' },
	]),
})
export const BlockSeriesArchive = defineBlock({
	thumbnail: '/block-thumbs/BlockSeriesArchive.svg',
	category: 'Listings',
	order: 117,
	name: 'BlockSeriesArchive',
	label: 'Series Archive',
	icon: 'collections_bookmark',
	schema,
	defaults: { title: 'Series and Symposia', variant: 'series' },
})
</script>
<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink } from 'vue-router'

import { Icon, Image, Skeleton } from '@bagelink/vue'
import { useCollection } from '@/composables/useDatastore'
import { articleTitleText, sanitizeArticleTitleHtml } from '@/utils/articleHtml'
import { displayImage } from '@/utils/image'

type ArchiveVariant = 'series' | 'printable'

interface CollectionItem {
	_id?: string
	title?: string
	slug?: string
	image?: string
	metaTitle?: string
	teaser?: string
	teaserHtml?: string
	publishDate?: string
}

/** Printable readers live in the `pages` collection (one row per PDF reader). */
interface ReaderItem {
	_id?: string
	name?: string
	slug?: string
	topTitle?: string
	image?: string
	pdf?: string
	bottomText?: string
}

/** One entry in the archive's sidebar list and card grid. */
interface ArchiveCard {
	_id?: string
	slug?: string
	title: string
	type?: string
	image?: string
	link: string
	teaserHtml?: string
	cta?: string
}



const props = withDefaults(defineProps<{ title?: string; variant?: ArchiveVariant }>(), {
	title: 'Series and Symposia',
	variant: 'series',
})

const isPrintable = computed(() => props.variant === 'printable')

const { items: series, loading: seriesLoading } = useCollection<CollectionItem>('series', {
	limit: 1000,
	all: true,
})
const { items: symposia, loading: symposiaLoading } = useCollection<CollectionItem>('symposia', {
	limit: 1000,
	all: true,
})
const { items: readers, loading: readersLoading } = useCollection<ReaderItem>('pages', {
	limit: 1000,
	all: true,
})

const loading = computed(() =>
	isPrintable.value
		? readersLoading.value
		: seriesLoading.value || symposiaLoading.value,
)

const archiveItems = computed<ArchiveCard[]>(() => {
	const seriesItems = series.value.map(item => ({
		...item,
		type: item.metaTitle || 'Series',
		link: `/series/${item.slug}`,
	}))
	const symposiumItems = symposia.value.map(item => ({
		...item,
		type: item.teaser || 'Symposium',
		link: `/symposium/${item.slug}`,
	}))
	const items = isPrintable.value ? [] : [...seriesItems, ...symposiumItems]
	return items
		.sort((a, b) => new Date(b.publishDate || 0).getTime() - new Date(a.publishDate || 0).getTime())
		.map(item => ({ ...item, title: item.title || '' }))
})

/** Only rows that actually carry a PDF are printable readers; each card links to it. */
const printableItems = computed<ArchiveCard[]>(() =>
	readers.value
		.filter(reader => typeof reader.pdf === 'string' && reader.pdf.trim().length > 0)
		.map(reader => ({
			_id: reader._id,
			slug: reader.slug || '',
			title: reader.name || '',
			type: reader.topTitle || '',
			image: reader.image || '',
			link: reader.pdf as string,
			cta: reader.bottomText || 'Download',
		})),
)

/** Same two-column archive layout for both variants — only the card target differs. */
const displayItems = computed<ArchiveCard[]>(() =>
	isPrintable.value ? printableItems.value : archiveItems.value,
)

function itemId(item: { slug?: string; _id?: string }) {
	return `collection-${item.slug || item._id}`
}

function scrollToItem(item: { slug?: string; _id?: string }) {
	const target = document.getElementById(itemId(item))
	if (!target) return
	target.scrollIntoView({ behavior: 'smooth', block: 'start' })
}
</script>

<template>
	<section class="pt-4 pb-3 m_pt-2 m_pb-2">
		<h1
			class="w1100px my-0 txt40 regular line-height-11 balance txt-center pb-3 m_px-1 m_txt32 m_line-height-12 m_pb-2"
		>
			{{ props.title }}
		</h1>

		<div
			v-if="loading"
			class="series-archive w1100px grid gap-1 m_grid-template-columns-1 m_px-1"
			aria-label="Loading collections"
			aria-busy="true"
		>
			<aside class="bg-gray-100 p-1-25 m_hide">
				<Skeleton width="150px" height="28px" class="mb-1-5" />
				<Skeleton v-for="item in 9" :key="item" width="85%" height="20px" class="mb-1" />
			</aside>
			<div class="grid gap-1-5">
				<Skeleton v-for="item in 4" :key="item" width="100%" height="155px" />
			</div>
		</div>

		<div
			v-else-if="displayItems.length"
			class="series-archive w1100px grid gap-1 m_grid-template-columns-1 m_px-1"
		>
			<aside class="m_hide">
				<div class="sticky-4 bg-gray-100" style="--bgl-scrollbar-thumb: var(--bgl-gray-110)">
					<h2 class="m-0 txt24 regular line-height-13 balance p-1-25">
						{{ isPrintable ? 'Explore:' : 'Explore Topics:' }}
					</h2>
					<nav class="grid gap-1-25 overflow max-h-side h-100p p-1-25">
						<a
							v-for="item in displayItems"
							:key="item._id || item.slug"
							:href="`#${itemId(item)}`"
							class="decoration-none redhover txt16 line-height-13 light"
							@click.prevent="scrollToItem(item)"
						>
							<span v-html="sanitizeArticleTitleHtml(item.title)" />
						</a>
					</nav>
				</div>
			</aside>

			<div class="grid gap-1-5">
				<component
					:is="isPrintable ? 'a' : RouterLink"
					v-for="item in displayItems"
					:id="itemId(item)"
					:key="item._id || item.slug"
					v-bind="
						isPrintable
							? { href: item.link, target: '_blank', rel: 'noopener' }
							: { to: item.link }
					"
					class="collection-card display-flex bg-gray-100 decoration-none m_column hover"
				>
					<Image
						:src="displayImage(item.image || '/og.jpg', 'card')"
						:alt="articleTitleText(item.title)"
						ratio="4-3"
						class="w-30p cover flex-shrink-0 m_w-100p"
						lazy
					/>
					<div class="p-1-5 m_p-1">
						<p v-if="item.type" class="m-0 txt14 color-primary pb-05">
							{{ item.type }}
						</p>
						<h2
							class="m-0 txt28 regular line-height-14 balance m_txt22 m_line-height-13"
							v-html="sanitizeArticleTitleHtml(item.title)"
						/>
						<p
							v-if="isPrintable && item.cta"
							class="flex gap-05 m-0 pt-05 txt17 color-primary line-height-14"
						>
							<Icon name="download" class="line-height-0" />
							{{ item.cta }}
						</p>
						<div
							v-else-if="item.teaserHtml"
							class="pt-05 txt17 light line-height-14 pretty m_txt15"
							v-html="item.teaserHtml"
						/>
					</div>
				</component>
			</div>
		</div>
	</section>
</template>

<style scoped>
.series-archive {
	grid-template-columns: 240px minmax(0, 1fr);
}

.collection-card {
	scroll-margin-top: 7rem;
}
</style>
